import ChartWrapper from "./ChartWrapper";
import { CHART_COLORS } from "./colors";

interface GaugeChartData {
  format?: string;
  max?: number;
  min?: number;
  target?: number;
  unit?: string;
  value: number;
}

interface GaugeChartProps {
  data: GaugeChartData;
  title: string;
}

// 270° gauge sweep with a 90° gap at the bottom.
const RADIUS = 40;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
const SWEEP_FRACTION = 0.75; // 270° of the full circle
const ARC_LENGTH = CIRCUMFERENCE * SWEEP_FRACTION;

export default function GaugeChart({ data, title }: GaugeChartProps) {
  // Default to 0-100 range if not specified
  const min = data.min ?? 0;
  const max = data.max ?? 100;
  const normalizedValue = Math.min(Math.max(data.value, min), max);
  const valueFraction = max > min ? (normalizedValue - min) / (max - min) : 0;

  // Determine color based on value and target
  const getColor = () => {
    if (normalizedValue >= (data.target || max * 0.8)) {
      return CHART_COLORS.primary;
    } else if (normalizedValue >= max * 0.6) {
      return CHART_COLORS.tertiary;
    } else if (normalizedValue >= max * 0.4) {
      return CHART_COLORS.quaternary;
    } else {
      return CHART_COLORS.senary;
    }
  };

  const formatValue = (value: number, format?: string, unit?: string) => {
    const safeValue = value ?? 0;
    const suffix = unit && unit !== "%" ? ` ${unit}` : "";
    switch (format) {
      case "score":
        return `${safeValue.toFixed(1)}/10`;
      case "percentage":
        return `${safeValue.toFixed(1)}%`;
      case "currency":
        return `$${safeValue.toLocaleString(undefined, { maximumFractionDigits: 1 })}`;
      case "decimal":
        return `${safeValue.toFixed(2)}${suffix}`;
      case "number":
        return `${safeValue.toLocaleString(undefined, { maximumFractionDigits: 1 })}${suffix}`;
      default:
        return `${safeValue.toLocaleString(undefined, { maximumFractionDigits: 1 })}${suffix}`;
    }
  };

  return (
    <ChartWrapper title={title}>
      <div className="relative h-full flex flex-col items-center justify-center">
        <svg
          viewBox="0 0 100 100"
          className="h-full w-full max-h-[220px]"
          preserveAspectRatio="xMidYMid meet"
        >
          {/* Rotate so the 90° gap sits centered at the bottom */}
          <g transform="rotate(135 50 50)">
            {/* Background track */}
            <circle
              cx="50"
              cy="50"
              r={RADIUS}
              fill="none"
              stroke="#e5e7eb"
              strokeWidth="8"
              strokeLinecap="round"
              strokeDasharray={`${ARC_LENGTH} ${CIRCUMFERENCE}`}
            />
            {/* Value arc */}
            <circle
              cx="50"
              cy="50"
              r={RADIUS}
              fill="none"
              stroke={getColor()}
              strokeWidth="8"
              strokeLinecap="round"
              strokeDasharray={`${valueFraction * ARC_LENGTH} ${CIRCUMFERENCE}`}
            />
          </g>
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <div className="text-4xl font-bold text-gray-900">
            {formatValue(data.value, data.format, data.unit)}
          </div>
          {data.target && (
            <div className="text-sm text-gray-500 mt-1">
              Target: {formatValue(data.target, data.format, data.unit)}
            </div>
          )}
        </div>
      </div>
    </ChartWrapper>
  );
}
